import React from 'react'

import { TEST_ID_DATA_ACCESS_REQUEST_UNAVAILABLE } from 'modules/resultsV2/DataAccessRequestFormCard'
import { DataAccessRequestGenotypicQuerySection } from 'modules/resultsV2/DataAccessRequestGenotypicQuerySection'
import { DataAccessRequestMetadataSection } from 'modules/resultsV2/DataAccessRequestMetadataSection'
import { DataAccessRequestPhenotypicQuerySection } from 'modules/resultsV2/DataAccessRequestPhenotypicQuerySection'
import * as resultMetaStyles from 'modules/resultsV2/ResultMeta.styles'
import {
  DATA_ACCESS_REQUEST_DETAILS_UNAVAILABLE_MESSAGE,
  DEFAULT_DATA_ACCESS_REQUEST_DATASET_COLOR
} from 'modules/resultsV2/utils'
import { mapDataAccessRequestVerificationQuerySource } from 'services/data-access-requests/mapDataAccessRequestVerificationQuerySource'
import type { DataAccessRequestVerificationDetail } from 'services/data-access-requests/types'

export interface DataAccessRequestDetailsMetadataProps {
  dataAccessRequest: DataAccessRequestVerificationDetail
  includeQuerySections?: boolean
  metadataTestId?: string
  parentRequest?: DataAccessRequestVerificationDetail
  parentRequestLinkId?: string
  requestedAtOverride?: string
  showRejectionReason?: boolean
  showSharingMetadata?: boolean
}

export const DataAccessRequestDetailsMetadata = ({
  dataAccessRequest,
  includeQuerySections = true,
  metadataTestId,
  parentRequest,
  parentRequestLinkId,
  requestedAtOverride,
  showRejectionReason = false,
  showSharingMetadata
}: DataAccessRequestDetailsMetadataProps): JSX.Element => {
  const dataset = dataAccessRequest.dataset
  const requestedAt =
    requestedAtOverride ?? dataAccessRequest.formSubmission?.submittedAt ?? dataAccessRequest.createdAt

  if (!dataset) {
    return (
      <resultMetaStyles.ItemRow $hasBorder={false} data-testid={TEST_ID_DATA_ACCESS_REQUEST_UNAVAILABLE}>
        <resultMetaStyles.ItemSubtitle>{DATA_ACCESS_REQUEST_DETAILS_UNAVAILABLE_MESSAGE}</resultMetaStyles.ItemSubtitle>
      </resultMetaStyles.ItemRow>
    )
  }

  const querySource = mapDataAccessRequestVerificationQuerySource(dataAccessRequest)

  return (
    <>
      <DataAccessRequestMetadataSection
        requestingUserEmail={dataAccessRequest.requestingUser?.email}
        requestedAt={requestedAt}
        requesterOrganisationName={dataAccessRequest.requesterContext.organisationName}
        sourceWorkspaceName={dataset.sourceWorkspaceName}
        requesterWorkspaceName={dataAccessRequest.requesterContext.workspaceName}
        datasetName={dataset.datasetName}
        datasetColor={dataset.datasetColor ?? DEFAULT_DATA_ACCESS_REQUEST_DATASET_COLOR}
        requestStatus={dataAccessRequest.status}
        reviewSubStatus={dataAccessRequest.reviewSubStatus}
        studyId={dataAccessRequest.studyId}
        querySource={querySource}
        dataAccessRequestDetail={dataAccessRequest.dataAccessRequestDetail}
        sharing={dataAccessRequest.sharing}
        metadataTestId={metadataTestId}
        parentRequest={parentRequest}
        parentRequestLinkId={parentRequestLinkId}
        rejectedAt={showRejectionReason ? dataAccessRequest.verifiedAt : undefined}
        rejectedByEmail={showRejectionReason ? dataAccessRequest.verifiedBy?.email : undefined}
        rejectionReason={showRejectionReason ? dataAccessRequest.rejectionReason ?? null : undefined}
        showRejectionReason={showRejectionReason}
        showSharingMetadata={showSharingMetadata}
      />
      {includeQuerySections ? (
        <>
          <DataAccessRequestGenotypicQuerySection querySource={querySource} />
          <DataAccessRequestPhenotypicQuerySection querySource={querySource} />
        </>
      ) : null}
    </>
  )
}
